/**
 * PsicoSalud — Motor de formularios dinámicos (Módulo 4).
 *
 * Renderiza cualquier escala definida en JSON por el backend, sin
 * conocimiento previo de sus ítems. Agregar una escala nueva (BDI-II,
 * AUDIT, etc.) no requiere tocar este componente: basta con cargar su
 * definición, tal como se hace con PHQ-9 y GAD-7.
 *
 * Las respuestas se indexan por `scoring_key`, que es lo que el motor
 * de puntuación del backend espera recibir.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FormItem, FormTemplate } from '@/types';

export type Answers = Record<string, number | string>;

interface Props {
  template: FormTemplate;
  submitting?: boolean;
  onSubmit: (answers: Answers) => void;
}

export default function DynamicForm({ template, submitting = false, onSubmit }: Props) {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<Answers>({});
  const [showValidation, setShowValidation] = useState(false);

  const items = useMemo(
    () =>
      [...template.sections]
        .sort((a, b) => a.order_index - b.order_index)
        .flatMap((section) => [...section.items].sort((a, b) => a.order_index - b.order_index)),
    [template],
  );

  const requiredKeys = items
    .filter((item) => item.is_required && item.scoring_key)
    .map((item) => item.scoring_key as string);

  const answeredRequired = requiredKeys.filter(
    (key) => answers[key] !== undefined && answers[key] !== '',
  ).length;
  const isComplete = answeredRequired === requiredKeys.length;

  const setAnswer = (key: string | null, value: number | string) => {
    if (!key) return;
    setAnswers((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = () => {
    if (!isComplete) {
      setShowValidation(true);
      return;
    }
    onSubmit(answers);
  };

  return (
    <div data-cy="dynamic-form">
      <div className="card-header">
        <div>
          <h2>{template.name}</h2>
          <p className="t-suave t-pequeno" style={{ margin: 0 }}>
            {template.description}
          </p>
        </div>
        <span className="badge badge-info">{template.code}</span>
      </div>

      <div style={{ margin: '0.8rem 0' }}>
        <div className="progress-bar">
          <div
            style={{
              width: `${requiredKeys.length ? (answeredRequired / requiredKeys.length) * 100 : 0}%`,
            }}
          />
        </div>
        <p className="t-pequeno t-suave" style={{ marginTop: '0.3rem' }} data-cy="form-progress">
          {t('forms.answered', { answered: answeredRequired, total: requiredKeys.length })}
        </p>
      </div>

      {showValidation && !isComplete && (
        <div className="notice notice-warning" role="alert" data-cy="form-incomplete">
          {t('forms.incomplete')}
        </div>
      )}

      {[...template.sections]
        .sort((a, b) => a.order_index - b.order_index)
        .map((section) => (
          <section key={section.id} style={{ marginBottom: '1.2rem' }}>
            <h3>{section.title}</h3>
            {section.instructions && <p className="t-suave t-pequeno">{section.instructions}</p>}

            {[...section.items]
              .sort((a, b) => a.order_index - b.order_index)
              .map((item, index) => (
                <ItemField
                  key={item.id}
                  item={item}
                  index={index + 1}
                  value={item.scoring_key ? answers[item.scoring_key] : undefined}
                  onChange={(value) => setAnswer(item.scoring_key, value)}
                />
              ))}
          </section>
        ))}

      <div className="button-row">
        <button onClick={handleSubmit} disabled={submitting} data-cy="form-submit">
          {submitting ? t('common.loading') : t('forms.submit')}
        </button>
      </div>
    </div>
  );
}

interface ItemProps {
  item: FormItem;
  index: number;
  value: number | string | undefined;
  onChange: (value: number | string) => void;
}

function ItemField({ item, index, value, onChange }: ItemProps) {
  const { t } = useTranslation();
  const inputId = `item-${item.id}`;

  return (
    <div className="form-item" data-cy="form-item">
      <div className="question">
        <span className="index">{index}.</span>
        {item.question_text}
        {item.is_required && <span style={{ color: 'var(--rojo-600)' }}> *</span>}
      </div>

      {(item.item_type === 'LIKERT' || item.item_type === 'MULTIPLE_CHOICE') && (
        <div className="likert-options" role="radiogroup" aria-label={item.question_text}>
          {(item.options ?? []).map((option) => {
            const selected = value === option.value;
            return (
              <label
                key={String(option.value)}
                className={`likert-option${selected ? ' selected' : ''}`}
                data-cy="form-option"
              >
                <input
                  type="radio"
                  name={inputId}
                  value={String(option.value)}
                  checked={selected}
                  onChange={() => onChange(option.value)}
                />
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>
      )}

      {item.item_type === 'SCALE' && (
        <input
          id={inputId}
          type="range"
          min={0}
          max={10}
          value={typeof value === 'number' ? value : 0}
          onChange={(event) => onChange(Number(event.target.value))}
        />
      )}

      {item.item_type === 'NUMERIC' && (
        <input
          id={inputId}
          type="number"
          value={value ?? ''}
          onChange={(event) => onChange(Number(event.target.value))}
        />
      )}

      {item.item_type === 'TEXT' && (
        <textarea
          id={inputId}
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value)}
          placeholder={t('common.optional')}
        />
      )}

      {item.item_type === 'DATE' && (
        <input
          id={inputId}
          type="date"
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </div>
  );
}
